import { useEffect, useState } from "react";
import { PasswordInputIcon, PasswordInputType } from "../../utils/Enums";

/**
 * Props interface for PasswordInputElement.
 * 
 * @property {string} name - the name of the input field
 * @property {string} label - The label text for the input field.
 * @property {string} placeholder - The placeholder text for the input field.
 * @property {boolean} disabled - Whether the input field is disabled.
 * @property {PasswordInputIcon} image - The image URL for the toggle visibility icon.
 * @property {PasswordInputType} type - The type of the input field (password or text).
 * @property {string} inputValue - The current value of the input field.
 * @property {boolean} toggleTypeEnabled - Whether the toggle visibility icon is enabled.
 * @property {() => void} onIconClick - Callback function to handle icon clicks.
 * @property {(e: React.ChangeEvent<HTMLInputElement>) => void} onChange - Callback function to handle input changes.
 * @property {string} errorText - The error message to display.
 * @property {() => void} [onBlur] - Optional callback function to handle input blur events.
 */
interface Props {
    name?: string,
    label: string,
    placeholder: string,
    disabled: boolean,
    image: PasswordInputIcon,
    type: PasswordInputType,
    inputValue: string,
    toggleTypeEnabled: boolean,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    errorText: string,
    onBlur?: () => void,
}

/**
 * PasswordInputElement Component
 * 
 * A reusable password input component with label, placeholder, error message, and change/blur handlers.
 * 
 * @param {Props} props - The props for the component.
 * @returns {JSX.Element} The rendered password input element.
 */
const PasswordInputElement: React.FC<Props> = ({
    name,
    label,
    placeholder,
    disabled,
    image,
    type,
    inputValue,
    toggleTypeEnabled,
    onChange,
    errorText,
    onBlur,
}) => {
    const [inputType, setInputType] = useState<PasswordInputType>(type);
    const [icon, setIcon] = useState<PasswordInputIcon>(image)

    const handleTogglePasswordType = () => {
        setInputType((prevType) =>
            prevType === PasswordInputType.Password ? PasswordInputType.Text : PasswordInputType.Password
        );
        setIcon((prevIcon) => {
            switch (prevIcon) {
                case PasswordInputIcon.ShowDisabled:
                    return PasswordInputIcon.HideDisabled;
                case PasswordInputIcon.HideDisabled:
                    return PasswordInputIcon.ShowDisabled;
                case PasswordInputIcon.ShowEnabled:
                    return PasswordInputIcon.HideEnabled;
                case PasswordInputIcon.HideEnabled:
                    return PasswordInputIcon.ShowEnabled;
            }
        })
    };

    useEffect(() => {
        if (toggleTypeEnabled) {
            if (icon === PasswordInputIcon.ShowDisabled) {
                setIcon(PasswordInputIcon.ShowEnabled)
            } else {
                setIcon(PasswordInputIcon.HideEnabled)
            }
        } else {
            if (icon === PasswordInputIcon.ShowEnabled) {
                setIcon(PasswordInputIcon.ShowDisabled)
            } else if (icon === PasswordInputIcon.HideEnabled) {
                setIcon(PasswordInputIcon.HideDisabled)
            }
        }
    }, [toggleTypeEnabled])

    // ********************************************************************************

    return (
        <div className="w-full flex flex-col">
            <label
                className="font-semibold text-[12px] uppercase"
                htmlFor={`password-input-${label}`}
            >
                {label}
            </label>
            <div className={`w-full h-full flex items-center border-b border-solid ${disabled ? "border-gray-400/20" : "border-black"}`}>
                <input
                    name={name}
                    id={`password-input-${label}`}
                    className="w-[calc(100%-30px)] h-[40px] border-none outline-none font-normal text-[16px] pl-[5px] tracking-[1px] disabled:bg-white"
                    placeholder={placeholder}
                    value={inputValue}
                    type={inputType}
                    disabled={disabled}
                    onChange={onChange}
                    onBlur={onBlur}
                />
                <img
                    className={`w-[25px] mr-[5px] ${toggleTypeEnabled && "cursor-pointer"}`}
                    onClick={toggleTypeEnabled ? handleTogglePasswordType : undefined}
                    src={icon}
                    alt="Toggle visibility"
                />
            </div>
            {errorText && <div className="font-semibold text-[12px] leading-[17px] mt-[10px] text-holifya-red" aria-live="polite">
                {errorText}
            </div>}
        </div>
    )
}

export default PasswordInputElement
